import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { useAuth } from "@/lib/auth";
import { Save, Settings as SettingsIcon, Bell, Mail, Building2, Inbox, ChevronDown, ChevronRight, Trash2, BookOpen, ExternalLink } from "lucide-react";
import MockedBanner from "@/components/MockedBanner";
import InboxConnectionPanel from "@/components/InboxConnectionPanel";
import SmtpConfigPanel from "@/components/SmtpConfigPanel";
import WorkflowsSection from "@/components/WorkflowsSection";
import { invalidateAppSettingsCache } from "@/hooks/useAppSettings";

export default function SettingsPage() {
  const { user } = useAuth();
  const canEdit = user.role === "admin";
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/settings")
      .then(({ data }) => setForm(data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(""); setErr("");
    const payload = { ...form };
    // These fields are owned by their dedicated editors (WorkflowsSection,
    // SmtpConfigPanel, ImapSettingsSection). The General Settings save must
    // NEVER bundle them — otherwise stale snapshots can re-submit older
    // values and trip backend validation (e.g. Order_Stages must match the
    // canonical 6-stage set). Strip them so this save only writes the
    // fields actually rendered on this page.
    [
      "id", "created_date", "updated_date",
      "Order_Stages", "Docket_Statuses", "PO_Statuses",
      "Order_Stage_Labels", "Docket_Status_Labels", "PO_Status_Labels", "Portal_Order_Stage_Labels",
    ].forEach((k) => { delete payload[k]; });
    ["Default_Discount_Percent", "Default_Pickup_Lead_Days", "Default_Cost_Per_Pallet"].forEach((k) => {
      if (payload[k] !== undefined && payload[k] !== null && payload[k] !== "") payload[k] = Number(payload[k]);
    });
    try {
      const { data } = await api.patch("/settings", payload);
      setForm((prev) => ({ ...prev, ...data })); setMsg("Saved."); setTimeout(() => setMsg(""), 2500);
      invalidateAppSettingsCache();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  if (!form) return <div className="text-xs text-zinc-500">Loading settings…</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">General Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">Org-wide defaults, notifications, and outgoing email configuration.</p>
      </div>

      <Link
        to="/manual"
        data-testid="settings-user-manual-link"
        className="es-card p-4 flex items-center gap-3 hover:border-brand-300 transition-colors group"
      >
        <div className="w-10 h-10 rounded-lg bg-brand-50 ring-1 ring-brand-200 flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-4 h-4 text-brand-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-zinc-900">User Manual</div>
          <p className="text-xs text-zinc-500">Quick reference for every page and workflow. Print or save as PDF from the manual.</p>
        </div>
        <ExternalLink className="w-4 h-4 text-zinc-400 group-hover:text-brand-700" />
      </Link>

      <EmailMockedBannerIfNeeded />

      <SectionCard icon={Building2} title="Business" subtitle="Identifies the company across documents and emails.">
        <Field label="Company Name" name="Company_Name" form={form} u={u} canEdit={canEdit} />
        <Row>
          <Field label="Default Discount %" name="Default_Discount_Percent" type="number" form={form} u={u} canEdit={canEdit} />
          <Field label="Default Pickup Lead Days" name="Default_Pickup_Lead_Days" type="number" form={form} u={u} canEdit={canEdit} />
        </Row>
        <Field label="Default Cost / Extra Pallet ($)" name="Default_Cost_Per_Pallet" type="number" form={form} u={u} canEdit={canEdit} />
        <p className="text-[11px] text-zinc-500 -mt-3">
          Applied as the default <span className="font-medium">Each Extra Pallet</span> rate when you add a new
          delivery zone. The first pallet is covered by the zone&apos;s Base price.
        </p>
      </SectionCard>

      <WorkflowsSection />

      <SectionCard icon={Building2} title="Reseller Portal" subtitle="Controls what resellers see in their portal." id="portal">
        <Toggle
          label="Show pricing on portal"
          hint="When OFF (default), resellers only see live stock levels — MSRP, discount, your price, line totals and the delivery / quote sidebar are hidden across the portal. Turn ON when you're ready to expose pricing."
          checked={!!form.Portal_Show_Pricing}
          onChange={(v) => u("Portal_Show_Pricing", v)}
          disabled={!canEdit}
        />
      </SectionCard>

      <SectionCard icon={Mail} title="Email" subtitle="Outgoing sender, alert routing, and SMTP credentials." id="email">
        <Field label="From Email (display)" name="From_Email" type="email" form={form} u={u} canEdit={canEdit} />
        <Field label="Low-Stock Notification Email" name="Low_Stock_Notification_Email" type="email" form={form} u={u} canEdit={canEdit} />
        <SignatureField form={form} u={u} canEdit={canEdit} />
        <div className="border-t border-zinc-100 pt-4 mt-1">
          <h3 className="text-sm font-semibold text-zinc-900 mb-2 flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5 text-brand-600" /> SMTP credentials
          </h3>
          <SmtpConfigPanel />
        </div>
      </SectionCard>

      <SectionCard icon={Bell} title="Notifications" subtitle="Choose which automated alerts to send.">
        <Toggle
          label="Notify on low stock"
          hint="Sends a daily roundup when products drop below their warning threshold."
          checked={!!form.Notify_On_Low_Stock}
          onChange={(v) => u("Notify_On_Low_Stock", v)}
          disabled={!canEdit}
        />
        <Toggle
          label="Notify on every new order"
          hint="One email per new order created via Purchase Orders or manually."
          checked={!!form.Notify_On_New_Order}
          onChange={(v) => u("Notify_On_New_Order", v)}
          disabled={!canEdit}
        />
      </SectionCard>

      <ImapSettingsSection
        username={form.IMAP_USERNAME}
        enabled={!!form.PO_Email_Ingestion_Enabled}
        onToggle={(v) => u("PO_Email_Ingestion_Enabled", v)}
        canEdit={canEdit}
      />

      {canEdit && <DataMaintenanceSection />}

      {err && <div className="es-card p-4 text-sm text-red-700">{err}</div>}

      {canEdit && (
        <div className="flex items-center justify-end gap-3 sticky bottom-2 z-10">
          {msg && <span className="text-sm text-green-700 font-medium">{msg}</span>}
          <button data-testid="settings-save" disabled={saving} onClick={save} className="es-btn-primary shadow-md">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>; }

function EmailMockedBannerIfNeeded() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    api.get("/admin/smtp/status")
      .then(({ data }) => setShow(!(data?.configured && data?.has_password)))
      .catch(() => setShow(true)); // be conservative — if status fails, surface the warning
  }, []);
  if (!show) return null;
  return (
    <MockedBanner>
      Outgoing email runs over real SMTP once you fill in the SMTP credentials below — until
      then password-resets / welcome links are only logged to the backend stdout.
    </MockedBanner>
  );
}

function SignatureField({ form, u, canEdit }) {
  const toast = useToast();
  const value = form.Email_Signature || "";
  const imagePath = form.Email_Signature_Image_Path || "";
  const [uploading, setUploading] = useState(false);
  const imageUrl = imagePath
    ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/uploads/signatures/${imagePath.split("/").pop()}`
    : "";

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/email-signature/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      u("Email_Signature_Image_Path", data.path);
      toast.success(`Signature image saved (${Math.round(data.bytes / 1024)} KB).`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (e.message || "Upload failed"));
    } finally { setUploading(false); }
  };
  const removeImage = async () => {
    try {
      await api.delete("/admin/email-signature/image");
      u("Email_Signature_Image_Path", "");
      toast.success("Signature image removed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="space-y-1.5" data-testid="settings-email-signature">
      <label className="es-label flex items-center justify-between">
        <span>Email signature (appended to every outgoing email)</span>
        <span className="text-[10px] font-mono text-zinc-400">{value.length}/600</span>
      </label>
      <textarea
        value={value}
        onChange={(e) => u("Email_Signature", e.target.value.slice(0, 600))}
        disabled={!canEdit}
        rows={5}
        placeholder={"e.g.\nSarah Smith\nEdenstone Masonry\n+61 7 1234 5678\nedenstone.com.au"}
        className="es-input font-mono text-xs leading-snug"
        data-testid="settings-email-signature-input"
      />

      {/* Signature image upload */}
      <div className="flex items-start gap-3 pt-2 border-t border-zinc-100 mt-2">
        <div className="flex-1">
          <label className="es-label">Signature image (optional — PNG / JPEG, max 120 KB)</label>
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={(e) => uploadImage(e.target.files?.[0])}
              disabled={!canEdit || uploading}
              data-testid="settings-email-signature-image-input"
              className="text-xs file:mr-2 file:py-1 file:px-3 file:border file:border-zinc-300 file:rounded-md file:bg-white file:text-zinc-700 file:font-semibold hover:file:border-brand-500 file:cursor-pointer"
            />
            {imagePath && (
              <button
                type="button"
                onClick={removeImage}
                disabled={!canEdit}
                className="text-xs font-semibold text-red-600 hover:text-red-800"
                data-testid="settings-email-signature-image-remove"
              >Remove</button>
            )}
          </div>
          {uploading && <div className="text-[11px] text-zinc-500 mt-1">Uploading…</div>}
        </div>
        {imagePath && (
          <div className="w-32 h-20 border border-zinc-200 rounded-md bg-zinc-50 flex items-center justify-center p-1 overflow-hidden">
            <img
              src={imageUrl}
              alt="Signature preview"
              className="max-w-full max-h-full object-contain"
              data-testid="settings-email-signature-image-preview"
            />
          </div>
        )}
      </div>

      <p className="text-[11px] text-zinc-500 leading-relaxed pt-1">
        The text appears as plain text in the signature block; the image is embedded as
        a base64 data-URI so it displays in Gmail/Outlook without being blocked. Applies
        to password resets, welcome emails, low-stock alerts, new-order notifications,
        and marketing campaigns.
      </p>
    </div>
  );
}

function SectionCard({ icon: Icon, title, subtitle, children, id }) {
  return (
    <section id={id} className="es-card overflow-hidden">
      <div className="px-6 py-4 border-b border-zinc-100 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-brand-50 ring-1 ring-brand-200 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-brand-700" />
        </div>
        <div>
          <h2 className="font-semibold text-zinc-900">{title}</h2>
          {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="px-6 py-5 space-y-5">{children}</div>
    </section>
  );
}

function Field({ label, name, type = "text", form, u, canEdit }) {
  return (
    <div>
      <label className="es-label">{label}</label>
      <input
        type={type}
        disabled={!canEdit}
        value={form[name] ?? ""}
        onChange={(e) => u(name, e.target.value)}
        className="es-input"
        data-testid={`settings-${name}`}
      />
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, disabled }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 accent-brand-600"
      />
      <div className="min-w-0">
        <div className="text-sm font-medium text-zinc-800">{label}</div>
        {hint && <div className="text-xs text-zinc-500 mt-0.5">{hint}</div>}
      </div>
    </label>
  );
}

/**
 * Compact collapsed-by-default IMAP section. Shows a single status row.
 * Expands into the full InboxConnectionPanel only when the user clicks it.
 *
 * Master gated by `PO_Email_Ingestion_Enabled` — when off, only the master
 * toggle is shown and the rest of the IMAP UI (status row + config panel +
 * Refresh button on the PO Inbox page) is hidden. Backend polling is also
 * suspended (see imap_poller._ingestion_enabled).
 */
function ImapSettingsSection({ enabled, onToggle, canEdit }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    api.get("/purchase-orders/inbox/status")
      .then(({ data }) => setStatus(data))
      .catch(() => {});
  }, [enabled]);

  const configured = Boolean(status?.configured);
  const hasError = Boolean(status?.last_error) && configured;
  // Auto-expand if there's a problem so it's not hidden away
  const expanded = open || hasError;

  const dotClass = hasError
    ? "bg-red-500"
    : configured
    ? "bg-green-500"
    : "bg-zinc-300";
  const stateText = hasError ? "Connection error" : configured ? "Polling active" : "Not configured";

  return (
    <section className="es-card overflow-hidden" data-testid="settings-po-email-ingestion">
      <div className="px-6 py-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-brand-50 ring-1 ring-brand-200 flex items-center justify-center flex-shrink-0">
          <Inbox className="w-4 h-4 text-brand-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-semibold text-zinc-900">Email-to-PO ingestion</h2>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="po-email-ingestion-toggle">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={enabled}
                disabled={!canEdit}
                onChange={(e) => onToggle(e.target.checked)}
              />
              <span className="w-9 h-5 bg-zinc-200 rounded-full peer-checked:bg-brand-600 relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-4" />
              <span className="text-xs font-medium text-zinc-700">{enabled ? "On" : "Off"}</span>
            </label>
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            Master switch for the entire email pipeline — IMAP polling, the Refresh button on the PO Inbox,
            and email-source labels. <span className="font-medium">Off</span> means the inbox is fed by Upload + Paste only.
          </p>
        </div>
      </div>

      {enabled && (
        <>
          <button
            onClick={() => setOpen((v) => !v)}
            className="w-full flex items-center gap-3 px-6 py-3 hover:bg-zinc-50 text-left border-t border-zinc-100"
            data-testid="settings-imap-toggle"
          >
            <span className={`w-2 h-2 rounded-full ${dotClass}`} />
            <span className="text-xs text-zinc-700 font-medium">{stateText}</span>
            <span className="text-xs text-zinc-500 ml-2 truncate">
              <code className="text-zinc-700">{status?.username || "an IMAP mailbox"}</code>
            </span>
            <span className="ml-auto">
              {expanded ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
            </span>
          </button>
          {expanded && (
            <div className="px-6 pb-5 border-t border-zinc-100 pt-4">
              <InboxConnectionPanel />
            </div>
          )}
        </>
      )}
    </section>
  );
}


function DataMaintenanceSection() {
  const toast = useToast();
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const runCleanup = async () => {
    if (!window.confirm("Permanently delete every record that points to a deleted company / order / channel?\n\nThis includes orphan orders, dockets, status logs, messages, contacts and POs. Live data is untouched. Cannot be undone.")) {
      return;
    }
    setRunning(true);
    try {
      const { data } = await api.post("/admin/cleanup-orphans");
      setLastResult(data.cleaned);
      const total = Object.values(data.cleaned).reduce((s, n) => s + Number(n || 0), 0);
      toast.success(total > 0 ? `Cleaned up ${total} orphan record(s).` : "Nothing to clean — database is tidy.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="es-card overflow-hidden">
      <div className="px-6 py-4 border-b border-zinc-100 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-red-50 ring-1 ring-red-200 flex items-center justify-center flex-shrink-0">
          <Trash2 className="w-4 h-4 text-red-600" />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-base font-semibold text-zinc-900">Data Maintenance</h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Deleting a company now cascades — its contacts, orders, dockets, POs, channels, messages and pricing are removed automatically.
            Use the button below to clean up any records that were orphaned <em>before</em> the cascade rule existed.
          </p>
        </div>
      </div>
      <div className="px-6 py-5 space-y-3">
        <button
          onClick={runCleanup}
          disabled={running}
          data-testid="settings-cleanup-orphans-btn"
          className="inline-flex items-center gap-2 h-10 px-4 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg disabled:opacity-60"
        >
          <Trash2 className="w-4 h-4" />
          {running ? "Cleaning…" : "Delete orphan test data"}
        </button>
        {lastResult && (
          <div className="text-xs font-mono text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 max-h-48 overflow-y-auto">
            {Object.entries(lastResult).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 py-0.5">
                <span className="text-zinc-500">{k}</span>
                <span className={v > 0 ? "text-red-700 font-bold" : "text-zinc-400"}>{v}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
